import { z } from 'zod';

export const PlannerTaskStatus = z.enum([
  'todo',
  'in_progress',
  'completed',
  'cancelled',
]);
export type PlannerTaskStatus = z.infer<typeof PlannerTaskStatus>;

export const PlannerTaskPriority = z.enum([
  'none',
  'low',
  'medium',
  'high',
  'urgent',
]);
export type PlannerTaskPriority = z.infer<typeof PlannerTaskPriority>;

export const PlannerTaskRecurrenceFrequency = z.enum([
  'daily',
  'weekly',
  'monthly',
  'yearly',
]);
export type PlannerTaskRecurrenceFrequency = z.infer<
  typeof PlannerTaskRecurrenceFrequency
>;

export const PlannerTaskRecurrenceWeekday = z.enum([
  'MO',
  'TU',
  'WE',
  'TH',
  'FR',
  'SA',
  'SU',
]);
export type PlannerTaskRecurrenceWeekday = z.infer<
  typeof PlannerTaskRecurrenceWeekday
>;

export const PlannerTaskRecurrenceEnd = z.discriminatedUnion('type', [
  z.object({ type: z.literal('never') }).strict(),
  z
    .object({
      type: z.literal('count'),
      count: z.number().int().min(2).max(500),
    })
    .strict(),
  z
    .object({
      type: z.literal('until'),
      date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .refine(isValidDate, 'Use a valid recurrence end date'),
    })
    .strict(),
]);
export type PlannerTaskRecurrenceEnd = z.infer<
  typeof PlannerTaskRecurrenceEnd
>;

export const PlannerTaskReminderState = z.enum([
  'none',
  'pending',
  'fired',
  'acknowledged',
  'dismissed',
]);
export type PlannerTaskReminderState = z.infer<
  typeof PlannerTaskReminderState
>;

export const PlannerTaskView = z.enum([
  'all',
  'today',
  'upcoming',
  'overdue',
]);
export type PlannerTaskView = z.infer<typeof PlannerTaskView>;

export const PlannerLinkKind = z.enum([
  'thread',
  'contact',
  'event',
  'task',
]);
export type PlannerLinkKind = z.infer<typeof PlannerLinkKind>;

export const PlannerLinkRef = z
  .object({
    kind: PlannerLinkKind,
    id: z.number().int().positive(),
  })
  .strict();
export type PlannerLinkRef = z.infer<typeof PlannerLinkRef>;

export const PlannerLink = PlannerLinkRef.extend({
  label: z.string(),
  available: z.boolean(),
});
export type PlannerLink = z.infer<typeof PlannerLink>;

const TaskLinkRef = PlannerLinkRef.refine(
  (link) => link.kind !== 'task',
  'A task cannot link to another task in this version',
);

const PlannerLinkRefs = z
  .array(PlannerLinkRef)
  .max(20)
  .refine(
    (links) =>
      new Set(links.map((link) => `${link.kind}:${link.id}`)).size ===
      links.length,
    'Links must be unique',
  );

const TaskLinkRefs = z
  .array(TaskLinkRef)
  .max(20)
  .refine(
    (links) =>
      new Set(links.map((link) => `${link.kind}:${link.id}`)).size ===
      links.length,
    'Links must be unique',
  );

const PlannerTaskRecurrenceRuleFields = {
  frequency: PlannerTaskRecurrenceFrequency,
  interval: z.number().int().min(1).max(365),
  timeZone: z
    .string()
    .trim()
    .min(1)
    .max(100)
    .refine(isSupportedTimeZone, 'Use a supported IANA time zone'),
  weekdays: z
    .array(PlannerTaskRecurrenceWeekday)
    .max(7)
    .refine(
      (days) => new Set(days).size === days.length,
      'Weekdays must be unique',
    )
    .default([]),
  end: PlannerTaskRecurrenceEnd.default({ type: 'never' }),
} as const;

export const PlannerTaskRecurrence = z
  .object({
    ...PlannerTaskRecurrenceRuleFields,
    seriesId: z.string().uuid(),
    index: z.number().int().nonnegative(),
    anchorAt: z.number().int().nonnegative(),
  })
  .strict()
  .superRefine(validateTaskRecurrence);
export type PlannerTaskRecurrence = z.infer<typeof PlannerTaskRecurrence>;

const PlannerTaskRecurrenceInput = z
  .object(PlannerTaskRecurrenceRuleFields)
  .strict()
  .superRefine(validateTaskRecurrence);

export const PlannerTaskSeriesScope = z.enum(['this', 'following']);
export type PlannerTaskSeriesScope = z.infer<typeof PlannerTaskSeriesScope>;

export const PlannerTaskSyncState = z.enum([
  'pending',
  'synced',
  'error',
  'read_only',
]);
export type PlannerTaskSyncState = z.infer<typeof PlannerTaskSyncState>;

export const PlannerTaskSync = z.object({
  calendarId: z.number().int().positive(),
  calendarName: z.string(),
  state: PlannerTaskSyncState,
  error: z.string().nullable(),
  lastSyncedAt: z.number().int().nonnegative().nullable(),
});
export type PlannerTaskSync = z.infer<typeof PlannerTaskSync>;

export const PlannerTask = z.object({
  id: z.number().int().positive(),
  title: z.string(),
  description: z.string(),
  status: PlannerTaskStatus,
  priority: PlannerTaskPriority,
  dueAt: z.number().int().nonnegative().nullable(),
  completedAt: z.number().int().nonnegative().nullable(),
  reminderAt: z.number().int().nonnegative().nullable(),
  reminderState: PlannerTaskReminderState,
  reminderSnoozedUntil: z.number().int().nonnegative().nullable(),
  reminderAcknowledgedAt: z.number().int().nonnegative().nullable(),
  recurrence: PlannerTaskRecurrence.nullable(),
  showOnCalendar: z.boolean(),
  sync: PlannerTaskSync.nullable(),
  links: z.array(PlannerLink),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
});
export type PlannerTask = z.infer<typeof PlannerTask>;

export const PlannerTaskCreate = z
  .object({
    title: z.string().trim().min(1).max(300),
    description: z.string().max(50_000).optional(),
    status: PlannerTaskStatus.optional(),
    priority: PlannerTaskPriority.optional(),
    dueAt: z.number().int().nonnegative().nullable().optional(),
    reminderAt: z.number().int().nonnegative().nullable().optional(),
    recurrence: PlannerTaskRecurrenceInput.nullable().optional(),
    showOnCalendar: z.boolean().optional(),
    syncCalendarId: z.number().int().positive().nullable().optional(),
    links: TaskLinkRefs.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.recurrence && value.dueAt == null) {
      ctx.addIssue({
        code: 'custom',
        path: ['dueAt'],
        message: 'A recurring task needs a due date',
      });
    }
  });
export type PlannerTaskCreate = z.infer<typeof PlannerTaskCreate>;

export const PlannerTaskUpdate = z
  .object({
    title: z.string().trim().min(1).max(300).optional(),
    description: z.string().max(50_000).optional(),
    status: PlannerTaskStatus.optional(),
    priority: PlannerTaskPriority.optional(),
    dueAt: z.number().int().nonnegative().nullable().optional(),
    reminderAt: z.number().int().nonnegative().nullable().optional(),
    recurrence: PlannerTaskRecurrenceInput.nullable().optional(),
    showOnCalendar: z.boolean().optional(),
    syncCalendarId: z.number().int().positive().nullable().optional(),
    seriesScope: PlannerTaskSeriesScope.optional(),
    links: TaskLinkRefs.optional(),
  })
  .strict()
  .refine(
    (value) => Object.keys(value).length > 0,
    'At least one task field is required',
  );
export type PlannerTaskUpdate = z.infer<typeof PlannerTaskUpdate>;

export const PlannerTaskList = z.object({
  tasks: z.array(PlannerTask),
  total: z.number().int().nonnegative(),
  openCount: z.number().int().nonnegative(),
  completedCount: z.number().int().nonnegative(),
  overdueCount: z.number().int().nonnegative(),
  dueTodayCount: z.number().int().nonnegative(),
  upcomingCount: z.number().int().nonnegative(),
  truncated: z.boolean(),
});
export type PlannerTaskList = z.infer<typeof PlannerTaskList>;

export const PlannerTaskReminderAction = z.discriminatedUnion('action', [
  z.object({ action: z.literal('acknowledge') }).strict(),
  z.object({ action: z.literal('dismiss') }).strict(),
  z
    .object({
      action: z.literal('snooze'),
      until: z.number().int().nonnegative(),
    })
    .strict(),
]);
export type PlannerTaskReminderAction = z.infer<
  typeof PlannerTaskReminderAction
>;

export const PlannerTaskReminderList = z.object({
  reminders: z.array(PlannerTask),
  count: z.number().int().nonnegative(),
});
export type PlannerTaskReminderList = z.infer<
  typeof PlannerTaskReminderList
>;

export const PlannerCalendarTask = z.object({
  taskId: z.number().int().positive(),
  title: z.string(),
  dueAt: z.number().int().nonnegative(),
  priority: PlannerTaskPriority,
  status: PlannerTaskStatus,
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});
export type PlannerCalendarTask = z.infer<typeof PlannerCalendarTask>;

export const PlannerCalendarTaskList = z.object({
  start: z.number().int().nonnegative(),
  end: z.number().int().positive(),
  tasks: z.array(PlannerCalendarTask),
});
export type PlannerCalendarTaskList = z.infer<
  typeof PlannerCalendarTaskList
>;

export const MAX_PLANNER_NOTE_ATTACHMENT_COUNT = 20;
export const MAX_PLANNER_NOTE_ATTACHMENT_BYTES = 25 * 1024 * 1024;
export const MAX_PLANNER_NOTE_ATTACHMENT_TOTAL_BYTES = 100 * 1024 * 1024;
export const MAX_PLANNER_NOTE_BODY_HTML_LENGTH = 500_000;

export const PlannerNoteAttachment = z.object({
  id: z.number().int().positive(),
  filename: z.string().min(1).max(255),
  contentType: z.string().min(1).max(255),
  size: z
    .number()
    .int()
    .nonnegative()
    .max(MAX_PLANNER_NOTE_ATTACHMENT_BYTES),
  createdAt: z.number().int().nonnegative(),
});
export type PlannerNoteAttachment = z.infer<typeof PlannerNoteAttachment>;

export const PlannerNote = z.object({
  id: z.number().int().positive(),
  title: z.string(),
  body: z.string(),
  bodyHtml: z.string().max(MAX_PLANNER_NOTE_BODY_HTML_LENGTH),
  pinned: z.boolean(),
  tags: z.array(z.string()),
  attachments: z
    .array(PlannerNoteAttachment)
    .max(MAX_PLANNER_NOTE_ATTACHMENT_COUNT),
  links: z.array(PlannerLink),
  createdAt: z.number().int().nonnegative(),
  updatedAt: z.number().int().nonnegative(),
});
export type PlannerNote = z.infer<typeof PlannerNote>;

const NoteFields = {
  title: z.string().trim().max(300),
  body: z.string().max(200_000),
  bodyHtml: z.string().max(MAX_PLANNER_NOTE_BODY_HTML_LENGTH),
  pinned: z.boolean(),
  tags: z.array(z.string().trim().min(1).max(40)).max(20),
  links: PlannerLinkRefs,
} as const;

export const PlannerNoteCreate = z
  .object({
    title: NoteFields.title.optional(),
    body: NoteFields.body.optional(),
    bodyHtml: NoteFields.bodyHtml.optional(),
    pinned: NoteFields.pinned.optional(),
    tags: NoteFields.tags.optional(),
    links: NoteFields.links.optional(),
  })
  .strict()
  .refine(
    (value) =>
      Boolean(
        value.title?.trim() ||
          value.body?.trim() ||
          value.bodyHtml?.replace(/<[^>]*>/g, '').trim(),
      ),
    'A note needs a title or some text',
  );
export type PlannerNoteCreate = z.infer<typeof PlannerNoteCreate>;

export const PlannerNoteUpdate = z
  .object({
    title: NoteFields.title.optional(),
    body: NoteFields.body.optional(),
    bodyHtml: NoteFields.bodyHtml.optional(),
    pinned: NoteFields.pinned.optional(),
    tags: NoteFields.tags.optional(),
    links: NoteFields.links.optional(),
  })
  .strict()
  .refine(
    (value) => Object.keys(value).length > 0,
    'At least one note field is required',
  );
export type PlannerNoteUpdate = z.infer<typeof PlannerNoteUpdate>;

export const PlannerNoteList = z.object({
  notes: z.array(PlannerNote),
  total: z.number().int().nonnegative(),
  pinnedCount: z.number().int().nonnegative(),
  tags: z.array(z.string()),
  truncated: z.boolean(),
});
export type PlannerNoteList = z.infer<typeof PlannerNoteList>;

export const PlannerBacklinkTargetKind = z.enum([
  'thread',
  'contact',
  'event',
]);
export type PlannerBacklinkTargetKind = z.infer<
  typeof PlannerBacklinkTargetKind
>;

export const PlannerBacklinkTask = PlannerTask.pick({
  id: true,
  title: true,
  status: true,
  priority: true,
  dueAt: true,
});
export type PlannerBacklinkTask = z.infer<typeof PlannerBacklinkTask>;

export const PlannerBacklinkNote = PlannerNote.pick({
  id: true,
  title: true,
  pinned: true,
  updatedAt: true,
});
export type PlannerBacklinkNote = z.infer<typeof PlannerBacklinkNote>;

export const PlannerBacklinks = z.object({
  tasks: z.array(PlannerBacklinkTask),
  notes: z.array(PlannerBacklinkNote),
  taskCount: z.number().int().nonnegative(),
  noteCount: z.number().int().nonnegative(),
  truncated: z.boolean(),
});
export type PlannerBacklinks = z.infer<typeof PlannerBacklinks>;

export const PlannerDeleteResponse = z.object({
  ok: z.literal(true),
});
export type PlannerDeleteResponse = z.infer<typeof PlannerDeleteResponse>;

export const PlannerTaskSyncRequest = z
  .object({
    calendarId: z.number().int().positive().optional(),
  })
  .strict();
export type PlannerTaskSyncRequest = z.infer<typeof PlannerTaskSyncRequest>;

export const PlannerTaskSyncResult = z.object({
  calendars: z.number().int().nonnegative(),
  imported: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  deleted: z.number().int().nonnegative(),
  pushed: z.number().int().nonnegative(),
  failures: z.array(
    z.object({
      calendarId: z.number().int().positive(),
      calendarName: z.string(),
      error: z.string(),
    }),
  ),
});
export type PlannerTaskSyncResult = z.infer<typeof PlannerTaskSyncResult>;

function validateTaskRecurrence(
  value: {
    frequency: PlannerTaskRecurrenceFrequency;
    weekdays: PlannerTaskRecurrenceWeekday[];
  },
  ctx: z.RefinementCtx,
): void {
  if (value.frequency !== 'weekly' && value.weekdays.length > 0) {
    ctx.addIssue({
      code: 'custom',
      path: ['weekdays'],
      message: 'Selected weekdays are only available for weekly tasks',
    });
  }
}

function isSupportedTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: value }).format(0);
    return true;
  } catch {
    return false;
  }
}

function isValidDate(value: string): boolean {
  const timestamp = Date.parse(`${value}T00:00:00Z`);
  return (
    Number.isFinite(timestamp) &&
    new Date(timestamp).toISOString().slice(0, 10) === value
  );
}
